import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";

const STORAGE_KEY = "kw_age_verified";
const MIN_AGE = 18;

export const hasVerifiedAge = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === "yes";
    } catch {
        return false;
    }
};

const calcAge = (dateStr) => {
    if (!dateStr) return null;
    const dob = new Date(dateStr);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
    return age;
};

export default function AgeGate({ onAccept }) {
    const [dob, setDob] = useState({ day: "", month: "", year: "" });
    const [denied, setDenied] = useState(false);
    const [error, setError] = useState("");

    const maxYear = new Date().getFullYear();
    const minYear = maxYear - 120;

    const dobString = useMemo(() => {
        const { day, month, year } = dob;
        if (!day || !month || !year) return "";
        const d = day.padStart(2, "0");
        const m = month.padStart(2, "0");
        if (year.length !== 4) return "";
        return `${year}-${m}-${d}`;
    }, [dob]);

    const age = useMemo(() => calcAge(dobString), [dobString]);

    const submit = (e) => {
        e.preventDefault();
        setError("");
        const d = parseInt(dob.day, 10);
        const m = parseInt(dob.month, 10);
        const y = parseInt(dob.year, 10);
        if (!d || !m || !y || y < minYear || y > maxYear || m < 1 || m > 12 || d < 1 || d > 31) {
            setError("Please enter a valid date of birth");
            return;
        }
        const check = new Date(`${dob.year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
        if (
            Number.isNaN(check.getTime()) ||
            check.getDate() !== d ||
            check.getMonth() + 1 !== m ||
            check.getFullYear() !== y
        ) {
            setError("Please enter a valid date of birth");
            return;
        }
        if (check > new Date()) {
            setError("Please enter a valid date of birth");
            return;
        }
        const a = calcAge(dobString);
        if (a === null || a < MIN_AGE) {
            setDenied(true);
            return;
        }
        try {
            localStorage.setItem(STORAGE_KEY, "yes");
        } catch {
            /* ignore */
        }
        onAccept?.();
    };

    const setField = (key) => (e) => {
        const raw = e.target.value.replace(/\D/g, "");
        const max = key === "year" ? 4 : 2;
        setDob((prev) => ({ ...prev, [key]: raw.slice(0, max) }));
    };

    const inputCls =
        "w-full bg-black border border-white/20 rounded-lg px-3 py-3 text-white text-center text-lg tracking-wider focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none";

    return (
        <AnimatePresence>
            <motion.div
                data-testid="age-gate"
                className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <motion.div
                    className="w-full max-w-sm bg-[#121212] border border-white/10 rounded-3xl p-7 text-center"
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 22, stiffness: 260 }}
                >
                    <div className="flex justify-center mb-5">
                        <Logo className="text-2xl" />
                    </div>

                    {!denied ? (
                        <form onSubmit={submit} data-testid="age-gate-form">
                            <div
                                data-testid="age-gate-title"
                                className="font-display text-3xl uppercase tracking-tight mb-2"
                            >
                                Enter your birthdate
                            </div>
                            <p className="text-white/60 text-sm mb-6 leading-relaxed">
                                You must be at least {MIN_AGE} years old to
                                enter the KYLIE WEST shop.
                            </p>

                            <div className="grid grid-cols-3 gap-3 mb-3 text-left">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">
                                        Day
                                    </label>
                                    <input
                                        data-testid="dob-day"
                                        inputMode="numeric"
                                        autoComplete="bday-day"
                                        placeholder="DD"
                                        value={dob.day}
                                        onChange={setField("day")}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">
                                        Month
                                    </label>
                                    <input
                                        data-testid="dob-month"
                                        inputMode="numeric"
                                        autoComplete="bday-month"
                                        placeholder="MM"
                                        value={dob.month}
                                        onChange={setField("month")}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1">
                                        Year
                                    </label>
                                    <input
                                        data-testid="dob-year"
                                        inputMode="numeric"
                                        autoComplete="bday-year"
                                        placeholder="YYYY"
                                        value={dob.year}
                                        onChange={setField("year")}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    data-testid="age-gate-error"
                                    className="text-sm text-[#FF5A00] mb-3"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                data-testid="age-gate-submit"
                                type="submit"
                                className="w-full py-3 mt-2 kw-orange-bg text-black font-bold rounded-xl uppercase tracking-wider kw-glow"
                            >
                                Enter
                            </button>

                            <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mt-5">
                                We only use your birthdate to verify your age
                            </p>
                        </form>
                    ) : (
                        <div data-testid="age-gate-denied">
                            <div className="font-display text-3xl uppercase tracking-tight mb-2 kw-orange">
                                Sorry
                            </div>
                            <p className="text-white/70 text-sm leading-relaxed mb-6">
                                You need to be at least {MIN_AGE} years old to
                                access this site
                                {age !== null && age >= 0
                                    ? ` (you entered age ${age})`
                                    : ""}
                                .
                            </p>
                            <button
                                onClick={() => {
                                    setDenied(false);
                                    setDob({ day: "", month: "", year: "" });
                                    setError("");
                                }}
                                className="text-xs uppercase tracking-widest text-white/50 hover:text-white"
                            >
                                Back
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
